import React, {useReducer, useEffect} from 'react'
import styled from 'styled-components'
import logonBackground from '../../assets/img/dark-modern-essential.jpg'
import logo from '../../assets/img/archibus-logo-name.svg'
import ListSelect from '../../components/ListSelect/ListSelect'
import * as Security from '../../api/archibus/SecurityApi'
import {useUserDispatch} from '../../context/UserContext'
import {logon} from '../../api/authentication'
import {useUIDispatch} from '../../context/UIContext'
import {useConfigDispatch} from '../../context/ConfigContext'
import {
  _L,
  useLocaleState,
  useLocaleDispatch
} from '../../context/LocaleContext'
import {getGuestUserAccount} from '../../utils/authentication'
import {initializeUser} from '../../App'
import {isServiceCatalog} from '../../utils/routing'
import * as ReactGA from 'react-ga'
import Spinner from '../../components/Spinner/Spinner'

const initialState = {
  userName: '',
  password: '',
  errors: {
    userName: null,
    password: null
  },
  touched: {
    userName: false,
    password: false
  },
  projects: [],
  project: null,
  serverError: '',
  isLoading: false,
  mode: 'USER'
}
const reducer = (state, action) => {
  switch (action.type) {
    case 'updateUser':
      return {
        ...state,
        userName: action.payload
      }

    case 'updatePassword':
      return {
        ...state,
        password: action.payload
      }
    case 'inputTouched':
      return {
        ...state,
        touched: {
          ...state.touched,
          ...action.payload
        }
      }

    case 'projects':
      return {
        ...state,
        projects: action.payload
      }

    case 'project':
      return {
        ...state,
        project: action.payload
      }

    case 'errors':
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      }

    case 'setServerError':
      return {
        ...state,
        serverError: action.payload
      }

    case 'setLoading':
      return {
        ...state,
        isLoading: action.payload.isLoading,
        mode: action.payload.mode
      }
  }
}

const Logon = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const localeState = useLocaleState()
  const {
    userName,
    password,
    touched,
    projects,
    project,
    serverError,
    isLoading,
    mode
  } = state
  const uiDispatch = useUIDispatch()
  const userDispatch = useUserDispatch()
  const localeDispatch = useLocaleDispatch()
  const configDispatch = useConfigDispatch()

  const validate = inputs => ({
    userName:
      inputs.userName.length > 0
        ? null
        : _L({key: 'User name is required', localeState}),
    password:
      inputs.password.length > 0
        ? null
        : _L({key: 'Password is required', localeState})
  })

  // isCatalogView is true when the Service Catalog is the primary app view.
  const isCatalogView = isServiceCatalog()

  // Move to effect?
  const errors = validate({userName, password})

  useEffect(() => {
    Security.getProjects().then(data => {
      let projects = data.map(project => ({
        value: project.id,
        label: project.title
      }))
      dispatch({type: 'projects', payload: projects})
      if (projects && projects.length) {
        dispatch({type: 'project', payload: projects[0].value})
      }
    })
  }, [])

  const doLogon = async ({username, password, project, mode}) => {
    dispatch({type: 'setLoading', payload: {isLoading: true, mode}})
    await logon({username, password, project})
    dispatch({type: 'setLoading', payload: {isLoading: false, mode}})
    ReactGA.set({
      userId: userName
    })

    const content = await initializeUser({
      userDispatch,
      uiDispatch,
      configDispatch,
      localeDispatch
    })

    uiDispatch({
      type: 'setContent',
      payload: {content: isCatalogView ? 'SERVICE_CATALOG' : content}
    })
  }

  const onLogon = async () => {
    const errors = validate({userName, password})

    const anyError = Object.keys(errors).some(x => errors[x])
    if (anyError) {
      dispatch({type: 'errors', payload: errors})
      dispatch({
        type: 'inputTouched',
        payload: {project: true, userName: true, password: true}
      })
    } else {
      try {
        await doLogon({username: userName, password, project, mode: 'USER'})
      } catch (error) {
        console.log(error)
        dispatch({
          type: 'setLoading',
          payload: {isLoading: false, mode: 'USER'}
        })
        dispatch({type: 'setServerError', payload: error})
      }
    }
  }

  const onLogonGuest = async () => {
    const {userName, password} = getGuestUserAccount()

    try {
      await doLogon({username: userName, password, project, mode: 'GUEST'})
    } catch (error) {
      console.log(error)
      dispatch({type: 'setLoading', payload: {isLoading: false, mode: 'GUEST'}})
      dispatch({type: 'setServerError', payload: error})
    }
  }

  // Do not display the project list. Use the first configured project
  // Keep the project list in the component for future development
  const displayProject = false

  return (
    <Container>
      <LogonContainer>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <LogonForm>
          <StyledTitle>{_L({key: 'Log in', localeState})}</StyledTitle>
          {displayProject && (
            <>
              <StyledLabel>{_L({key: 'Project', localeState})}</StyledLabel>
              <ListSelect
                options={projects}
                placeholder=""
                isSearchable={false}
                value={projects.filter(({value}) => value === project)}
                onChange={project =>
                  dispatch({type: 'project', payload: project.value})
                }
              />
            </>
          )}
          <StyledLabel>
            {_L({key: 'Username (case-insensitive)', localeState})}
          </StyledLabel>
          <StyledInput
            onChange={e =>
              dispatch({
                type: 'updateUser',
                payload: e.target.value.toUpperCase()
              })
            }
            onBlur={() =>
              dispatch({
                type: 'inputTouched',
                payload: {userName: true}
              })
            }
            value={state.userName}
            data-testid="logon-user-input"
          />
          <StyledErrorContainer>
            {errors.userName &&
              touched.userName && <StyledError>{errors.userName}</StyledError>}
          </StyledErrorContainer>
          <StyledLabel>
            {_L({key: 'Password (case-sensitive)', localeState})}
          </StyledLabel>
          <StyledInput
            onChange={e =>
              dispatch({type: 'updatePassword', payload: e.target.value})
            }
            value={state.password}
            onBlur={() =>
              dispatch({
                type: 'inputTouched',
                payload: {password: true}
              })
            }
            type="password"
            data-testid="logon-password-input"
          />
          <StyledErrorContainer>
            {errors.password &&
              touched.password && <StyledError>{errors.password}</StyledError>}
          </StyledErrorContainer>
          <StyledSignInButton onClick={onLogon} data-testid="logon-sign-in-btn">
            {isLoading && mode === 'USER' ? (
              <Spinner height={26} width={26} />
            ) : (
              _L({key: 'Log in', localeState})
            )}
          </StyledSignInButton>
          <StyledText>{_L({key: 'OR', localeState})}</StyledText>
          <StyledGuestButton
            onClick={onLogonGuest}
            data-testid="logon-sign-in-guest-btn"
          >
            {isLoading && mode === 'GUEST' ? (
              <Spinner color="#3367d6" height={26} width={26} />
            ) : (
              _L({key: 'Log in as Guest', localeState})
            )}
          </StyledGuestButton>
          <ServerErrorContainer>
            <ServerError>{serverError}</ServerError>
          </ServerErrorContainer>
        </LogonForm>
      </LogonContainer>
    </Container>
  )
}

export default Logon

const Container = styled.div`
  height: 100vh;
  width: 100vw;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${logonBackground});
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
`

const LogonContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const LogonForm = styled.div`
  flex: 0 1 auto;
  width: 380px;
  border-radius: 6px;
  background-color: #fff;
  box-shadow: 0 0 44px 0 #202d40;
  padding: 20px;
  
  @media (max-width: 420px) {
    width: calc(100vw - 40px) !important;
  }
`

const LogoContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`

const Logo = styled.div`
  flex: 1;
  height: 75px;
  background-size: auto;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(${logo});
`

const StyledInput = styled.input`
  width: 100%;
  height: 46px;
  background-color: #f3f5fb;
  border: 0;
  border-radius: 6px;
  padding-left: 16px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #000000;
  box-sizing: border-box;
`

const StyledLabel = styled.label`
  font-size: 12px;
  color: #a4a9b7;
  margin-bottom: 5px;
`

const StyledButton = styled.button`
  margin: 16px 0 0 0;
  height: 48px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  border: none;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
`

const StyledSignInButton = styled(StyledButton)`
  color: #eeeeee;
  background-color: #0354FF;
`

const StyledGuestButton = styled(StyledButton)`
  color: #0354FF;
  background-color: white;
  border: 1px solid #0354FF;
  margin: 0;
`
const StyledError = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ff2600;
`
const StyledErrorContainer = styled.div`
  height: 16px;
`

const ServerErrorContainer = styled.div`
  min-height: 28px;
  display: flex;
  align-items: center;
  padding: 4px 6px 0 6px;
`

const ServerError = styled(StyledError)`
  font-size: 16px;
`

const StyledText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
`

const StyledTitle = styled.div`
  font-size: 24px;
  color: black;
  font-weight: 600;
  padding: 12px 0 12px 0;
  text-align: center;
  letter-spacing: -0.5px;
`
